import { type Components } from '@mui/material/styles'
import type { Tokens, Theme } from '../augmentation'
import { spacing } from '../spacing'
import { fontSizeBody1, fontSizeBody2, fontSizeCaption1 } from '../typography'
import { type ComponentCreation } from './types'

const paddingInputLarge = spacing(3.25, 1, 1.5)
const paddingInputMedium = spacing(2.75, 1, 1)

const shrunkLabelTopOffset = spacing(1.5)

export const createMuiInput = ({ palette, misc }: Pick<Tokens, 'palette' | 'misc'>): Components<Theme> =>
  ({
    MuiTextField: {
      styleOverrides: {
        root: ({ ownerState }) => ({
          // For outline (default) variant
          '& .MuiOutlinedInput-root': {
            paddingLeft: spacing(1),
            paddingRight: spacing(2),

            backgroundColor: palette.components.input.background,
            borderRadius: misc.borderRadius(),
          },

          '& .MuiSvgIcon-root': {
            fontSize: spacing(2.5),
          },

          // Our three sizes
          '& .MuiInputBase-inputSizeSmall': {
            minHeight: spacing(5),
            boxSizing: 'border-box',
          },
          '& .MuiInputBase-inputSizeMedium': {
            minHeight: spacing(6),
            boxSizing: 'border-box',
          },
          '& .MuiInputBase-inputSizeLarge': {
            minHeight: spacing(7),
            boxSizing: 'border-box',
          },

          // Disabled
          '& .MuiInputBase-root.Mui-disabled': {
            backgroundColor: palette.action.disabledBackground,
          },

          // With label (medium and large have a label, medium is our default)
          '&:has(label) .MuiInputBase-root .MuiInputBase-input': {
            lineHeight: 1,
            height: spacing(2),
            padding: paddingInputMedium,
          },

          '& .MuiInputBase-root textarea.MuiInputBase-inputMultiline': {
            lineHeight: 1.4,
          },

          '&:has(label.MuiInputLabel-sizeLarge) .MuiInputBase-root .MuiInputBase-input': {
            lineHeight: 1,
            padding: paddingInputLarge,
          },

          // without a label
          '&:not(:has(label)) .MuiInputBase-input': {
            lineHeight: 1,
            padding: spacing(1.5, 1),
            fontSize: spacing(2),
          },

          '& .MuiOutlinedInput-notchedOutline': {
            borderColor: palette.dark.light,
            padding: spacing(0, 1),
          },

          '& .MuiInputBase-input': {
            color: palette.text.primary,

            '&::placeholder': {
              color: palette.text.placeholder,
              fontSize: fontSizeBody2,
              opacity: 1,
            },
          },

          '& .MuiInputBase-sizeLarge .MuiInputBase-input::placeholder': {
            fontSize: fontSizeBody1,
          },

          '& .MuiInputBase-adornedStart': {
            paddingLeft: spacing(2),
          },

          '& .MuiInputBase-input ~ .DhlIconButton:last-of-type': {
            marginRight: spacing(-1.375),
          },

          '& .MuiFormHelperText-root': {
            marginLeft: spacing(0),
          },

          ...(ownerState.size !== 'small' && {
            // UX doesn't want people to use the prop even, so when not on small we adjust it
            'label.MuiInputLabel-shrink + .MuiInputBase-adornedStart': {
              alignItems: 'baseline',

              // when start/end node is text we keep it at our baseline
              // but when its an svg we are going to correct it, before we move to bottom

              // target the svg béfore the input: (startAdornment)
              'svg:has(+ input)': {
                alignSelf: 'center',
                // this spacing is Equal to the translateY of the label when shrunk
                marginTop: shrunkLabelTopOffset,
              },
            },
          }),
        }),
      },
      variants: [
        {
          props: { size: 'large' },
          style: {
            '& .MuiInputBase-root': {
              minHeight: spacing(7),
            },
          },
        },
      ],
    },

    MuiInputBase: {
      defaultProps: {
        // https://mui.com/material-ui/react-text-field/#performance
        disableInjectingGlobalStyles: true,
      },
      styleOverrides: {
        root: {
          '&.Mui-readOnly': {
            backgroundColor: palette.action.disabledBackground,

            '& fieldset': {
              borderColor: 'transparent',
            },

            '& .MuiOutlinedInput-input': {
              color: palette.text.tertiary,
            },

            '& .MuiSelect-select': {
              backgroundColor: palette.action.disabledBackground,
            },
          },

          '.MuiInputBase-multiline > .MuiInputBase-input': {
            marginTop: spacing(1),
            // FIXME: see why now single input wins because of sizes (maybe not nicely fixable)...
            padding: `${spacing(0, 1)}!important`,
          },
        },

        multiline: {
          [`&:not(.MuiInputBase-inputHiddenLabel)`]: {
            lineHeight: 1,
            padding: spacing(1.5, 1, 1),
          },
        },

        input: {
          color: palette.text.primary,

          '&:-webkit-autofill': {
            WebkitBoxShadow: '0 0 0 100px #F5F5F5 inset',
            WebkitTextFillColor: palette.text.primary,
            caretColor: palette.text.primary,
          },
        },
      },
      variants: [
        {
          props: { size: 'small' },
          style: { minHeight: spacing(5) },
        },
        {
          props: { size: 'medium' },
          style: { minHeight: spacing(6) },
        },
        {
          props: { size: 'large' },
          style: { minHeight: spacing(7) },
        },
      ],
    },

    MuiOutlinedInput: {
      defaultProps: {
        notched: false,
      },
      styleOverrides: {
        // TODO: check when doing SELECT if style can be higher defined
        root: {
          '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: palette.grey[800],
          },
          // focused
          '&.Mui-focused .MuiOutlinedInput-notchedOutline, &.Mui-focusVisible .MuiOutlinedInput-notchedOutline': {
            border: misc.borderStyle('grey.800', 2),
          },
          // error
          '&.Mui-error .MuiOutlinedInput-notchedOutline': {
            borderColor: palette.error.main,
          },

          // disabled
          '&.Mui-disabled .MuiOutlinedInput-notchedOutline': {
            color: palette.action.disabled,
            borderColor: palette.action.disabledBackground,
          },
        },

        notchedOutline: {
          border: misc.borderStyle('dark.light'),
          borderRadius: misc.borderRadius(),
        },
      },
    },

    // both for Select and TextField
    MuiInputLabel: {
      styleOverrides: {
        root: {
          fontSize: fontSizeBody2,
          color: palette.text.tertiary,
          top: spacing(-0.25),
          lineHeight: 1,
          maxWidth: `calc(100% - ${spacing(4)})`,

          '&.MuiInputLabel-shrink': {
            color: palette.text.tertiary,
            // same as original, but we need to ensure we use
            // the same offset for our startAdornment, in order
            // to have the proper alignment
            transform: `translate(1rem, ${shrunkLabelTopOffset}) scale(1)`,

            '&.Mui-error': {
              color: palette.error.main,
            },
          },

          '&.Mui-disabled': {
            color: palette.action.disabled,
          },
        },

        outlined: ({ ownerState }) => ({
          // small has never a label, guarded by our type
          ...(ownerState.size === 'medium' && {
            fontSize: fontSizeBody1,
            transform: 'translate(1rem, 1.125rem) scale(1)',

            '&.MuiInputLabel-shrink, &.Mui-error': {
              fontSize: fontSizeCaption1,
              transform: 'translate(1rem, 0.625rem) scale(1)',
            },
          }),

          ...(ownerState.size === 'large' && {
            top: spacing(-0.25),
            fontSize: fontSizeBody1,
            transform: 'translate(1rem, 1.375rem) scale(1)',

            '&.MuiInputLabel-shrink, &.Mui-error': {
              fontSize: fontSizeCaption1,
              transform: 'translate(1rem, 0.75rem) scale(1)',
            },
          }),
        }),

        shrink: {
          '&.MuiInputLabel-root': {
            transform: 'translate(1rem, 0.5rem) scale(1)',
          },
        },

        formControl: {
          '&.MuiInputLabel-sizeSmall': {
            transform: 'translate(1rem, 1rem) scale(1)',
          },

          // our selectbox is inside form control
          '&.MuiInputLabel-sizeSmall.MuiInputLabel-shrink': {
            display: 'none',
          },
        },
      },
    },
  }) satisfies ComponentCreation
